// src/pages/admin/AdminGuias.js
//
// Panel Superadmin: gestión de Guías de barrio.
// Lista con filtros + crear/editar en dialog + detalle con afiliados.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Stack, Button, Card, CardContent,
  TextField, MenuItem, InputAdornment, Table, TableHead, TableBody,
  TableRow, TableCell, IconButton, Chip, LinearProgress, Alert,
  Tooltip, Avatar, Grid,
} from '@mui/material';
import AddIcon           from '@mui/icons-material/Add';
import SearchIcon        from '@mui/icons-material/Search';
import ClearIcon         from '@mui/icons-material/Clear';
import EditIcon          from '@mui/icons-material/Edit';
import VisibilityIcon    from '@mui/icons-material/Visibility';
import PauseCircleIcon   from '@mui/icons-material/PauseCircle';
import PlayCircleIcon    from '@mui/icons-material/PlayCircle';
import BlockIcon         from '@mui/icons-material/Block';
import SupportAgentIcon  from '@mui/icons-material/SupportAgent';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import KeyIcon           from '@mui/icons-material/Key';

import { guias as apiGuias } from '../../api/guiasApi';
import { useApp } from '../../context/AppContext';
import DialogGuiaEditar  from '../../components/admin/DialogGuiaEditar';
import DialogGuiaDetalle from '../../components/admin/DialogGuiaDetalle';
import DialogCambiarPassword from '../../components/admin/DialogCambiarPassword';
import {
  useFiltrosUbicacion, SelectorDepartamento, SelectorMunicipio,
} from '../../components/admin/FiltrosUbicacion';

const ESTADOS = [
  { valor: '',         label: 'Todos' },
  { valor: 'ACTIVO',   label: 'Activos' },
  { valor: 'PAUSADO',  label: 'Pausados' },
  { valor: 'RETIRADO', label: 'Retirados' },
];

const colorEstado = (e) => e === 'ACTIVO' ? 'success' :
                           e === 'PAUSADO' ? 'warning' :
                           e === 'RETIRADO' ? 'default' : 'default';

export default function AdminGuias() {
  const { notificar } = useApp();
  const navigate = useNavigate();
  const [cargando, setCargando] = useState(true);
  const [lista, setLista] = useState([]);
  const [filtros, setFiltros] = useState({ buscar: '', estado: '' });
  const [filtrosGeo, setFiltrosGeo] = useState({ departamentoId: '', municipioId: '' });
  const [dialogEditar, setDialogEditar] = useState(null);   // null | {} | guia
  const [dialogDetalle, setDialogDetalle] = useState(null); // null | guiaId
  const [dialogPassword, setDialogPassword] = useState(null); // null | guia

  const geo = useFiltrosUbicacion({ cargarGuias: false });

  const limpiarFiltros = () => {
    setFiltros({ buscar: '', estado: '' });
    setFiltrosGeo({ departamentoId: '', municipioId: '' });
  };
  const hayFiltros = !!(filtros.buscar || filtros.estado
                        || filtrosGeo.departamentoId || filtrosGeo.municipioId);

  const cargar = async () => {
    setCargando(true);
    try {
      const q = {};
      if (filtros.buscar) q.buscar = filtros.buscar;
      if (filtros.estado) q.estado = filtros.estado;
      if (filtrosGeo.departamentoId) q.departamentoId = filtrosGeo.departamentoId;
      if (filtrosGeo.municipioId)    q.municipioId    = filtrosGeo.municipioId;
      const r = await apiGuias.listar(q);
      setLista(r || []);
    } catch (e) {
      notificar(e.mensajeUsuario || 'No se pudo cargar la lista de Guías.', 'error');
    } finally { setCargando(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, [filtros.estado, filtrosGeo.departamentoId, filtrosGeo.municipioId]);

  // Buscar con enter o cambio de estado
  const onBuscar = (e) => {
    if (e.key === 'Enter') cargar();
  };

  const cambiarEstado = async (guia, nuevoEstado) => {
    let motivo = null;
    if (nuevoEstado === 'RETIRADO') {
      motivo = window.prompt(`Retirar a ${guia.nombreCompleto}. Motivo:`);
      if (!motivo) return;
    }
    try {
      await apiGuias.cambiarEstado(guia.guiaId, nuevoEstado, motivo);
      notificar(`Estado de ${guia.nombreCompleto}: ${nuevoEstado}.`, 'success');
      cargar();
    } catch (e) {
      notificar(e.mensajeUsuario || 'No se pudo cambiar el estado.', 'error');
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <SupportAgentIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h4" fontFamily="Fraunces">Guías</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Personas que afilian negocios al ecosistema HolaVeci.
          </Typography>
        </Box>
        <Button variant="contained" size="large" startIcon={<AddIcon />}
                onClick={() => setDialogEditar({})}>
          Nuevo Guía
        </Button>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap"
                 useFlexGap sx={{ rowGap: 1.5 }}>
            <TextField
              size="small"
              placeholder="Buscar por nombre, cédula, teléfono o correo…"
              value={filtros.buscar}
              onChange={e => setFiltros(f => ({ ...f, buscar: e.target.value }))}
              onKeyDown={onBuscar}
              sx={{ flex: '1 1 300px', minWidth: 300 }}
              InputProps={{ startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              )}}
            />
            <TextField select size="small" label="Estado" sx={{ width: 150 }}
                       value={filtros.estado}
                       onChange={e => setFiltros(f => ({ ...f, estado: e.target.value }))}>
              {ESTADOS.map(e => <MenuItem key={e.valor} value={e.valor}>{e.label}</MenuItem>)}
            </TextField>
            <SelectorDepartamento geo={geo} valor={filtrosGeo.departamentoId}
              onChange={(v) => setFiltrosGeo(f => ({ ...f, departamentoId: v, municipioId: '' }))} />
            <SelectorMunicipio geo={geo} valor={filtrosGeo.municipioId}
              departamentoId={filtrosGeo.departamentoId}
              onChange={(v) => setFiltrosGeo(f => ({ ...f, municipioId: v }))} />
            <Button variant="outlined" size="small" onClick={cargar}>Buscar</Button>
            {hayFiltros && (
              <Button size="small" startIcon={<ClearIcon />} onClick={limpiarFiltros}
                      sx={{ ml: 'auto' }}>
                Limpiar
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      {cargando ? <LinearProgress /> : (
        lista.length === 0 ? (
          <Alert severity="info">
            No hay Guías registrados aún. Empieza agregando el primero con el botón "Nuevo Guía".
          </Alert>
        ) : (
          <Card>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Guía</TableCell>
                  <TableCell>Documento</TableCell>
                  <TableCell>Teléfono</TableCell>
                  <TableCell>Zona</TableCell>
                  <TableCell align="center">Radio</TableCell>
                  <TableCell align="center">Comisión</TableCell>
                  <TableCell align="center">Afiliados</TableCell>
                  <TableCell align="center">Estado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lista.map(g => (
                  <TableRow key={g.guiaId} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar src={g.fotoUrl} sx={{ bgcolor: 'primary.light', width: 32, height: 32 }}>
                          {g.nombreCompleto?.[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{g.nombreCompleto}</Typography>
                          {g.correo && <Typography variant="caption" color="text.secondary">{g.correo}</Typography>}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{g.tipoDocumento} {g.numeroDocumento}</Typography>
                    </TableCell>
                    <TableCell>{g.telefono}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{g.municipio || '—'}</Typography>
                      {g.barriosCubiertos &&
                        <Typography variant="caption" color="text.secondary">
                          {g.barriosCubiertos.split(',').slice(0, 2).join(', ')}
                          {g.barriosCubiertos.split(',').length > 2 && '…'}
                        </Typography>}
                    </TableCell>
                    <TableCell align="center">{g.radioOperacionKm} km</TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{g.porcentajeComision}%</Typography>
                      {g.mesesRecurrente > 0
                        ? <Typography variant="caption" color="text.secondary">{g.mesesRecurrente} meses</Typography>
                        : <Typography variant="caption" color="text.secondary">recurrente</Typography>}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={g.afiliadosActivos} size="small"
                            color={g.afiliadosActivos > 0 ? 'primary' : 'default'} />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={g.estado} size="small" color={colorEstado(g.estado)} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Ver detalle">
                        <IconButton size="small" onClick={() => setDialogDetalle(g.guiaId)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supervisar (panel, afiliados, comisiones, liquidaciones)">
                        <IconButton size="small" onClick={() => navigate(`/admin/guias/${g.guiaId}/supervision`)}>
                          <SupervisorAccountIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={g.aspNetUserId ? 'Cambiar contraseña' : 'Sin usuario de acceso'}>
                        <span>
                          <IconButton size="small" disabled={!g.aspNetUserId}
                                      onClick={() => setDialogPassword(g)}>
                            <KeyIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => setDialogEditar(g)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {g.estado === 'ACTIVO' && (
                        <Tooltip title="Pausar">
                          <IconButton size="small" onClick={() => cambiarEstado(g, 'PAUSADO')}>
                            <PauseCircleIcon fontSize="small" color="warning" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {g.estado === 'PAUSADO' && (
                        <Tooltip title="Reactivar">
                          <IconButton size="small" onClick={() => cambiarEstado(g, 'ACTIVO')}>
                            <PlayCircleIcon fontSize="small" color="success" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {g.estado !== 'RETIRADO' && (
                        <Tooltip title="Retirar">
                          <IconButton size="small" onClick={() => cambiarEstado(g, 'RETIRADO')}>
                            <BlockIcon fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )
      )}

      <DialogGuiaEditar
        abierto={dialogEditar !== null}
        guia={dialogEditar || {}}
        onCerrar={() => setDialogEditar(null)}
        onGuardado={() => { setDialogEditar(null); cargar(); }}
      />
      <DialogGuiaDetalle
        abierto={dialogDetalle !== null}
        guiaId={dialogDetalle}
        onCerrar={() => setDialogDetalle(null)}
      />
      <DialogCambiarPassword
        abierto={dialogPassword !== null}
        aspNetUserId={dialogPassword?.aspNetUserId}
        nombre={dialogPassword?.nombreCompleto}
        detalle={[dialogPassword?.correo, dialogPassword?.telefono].filter(Boolean).join(' · ')}
        onCerrar={() => setDialogPassword(null)}
        onCambiada={(m) => notificar(m, 'success')}
      />
    </Container>
  );
}
